import React, { useEffect, useState } from 'react';
import { Truck, HardHat, Tag, Check, Phone, Smartphone, Sparkles, HelpCircle, MapPin, CreditCard, Home } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { useT } from '../utils/i18n';
const PROMO_CODE = 'FIRST10';
const PHONE_NUMBER = '(206) 619-3742';
const ROTATE_MS = 5000;
export function AnnouncementBar() {
  const location = useLocation();
  const t = useT();
  const isContractors = location.pathname.includes('subcontractors') || location.pathname.includes('contractors');
  const [copied, setCopied] = useState(false);
  const [phoneOpen, setPhoneOpen] = useState(false);
  // Auto-hide the phone number 3s after it slides out
  useEffect(() => {
    if (!phoneOpen) return;
    const id = setTimeout(() => setPhoneOpen(false), 3000);
    return () => clearTimeout(id);
  }, [phoneOpen]);
  const handleCopy = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(PROMO_CODE);
      try {
        sessionStorage.setItem('promo_hint', PROMO_CODE);
      } catch {}
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {}
  };
  // Single static promo message — no rotation.
  const currentSlide = {
    key: 'promo',
    content: <>
        <span className="hidden sm:inline-flex items-center justify-center w-5 h-5 rounded-full bg-stone-900 text-amber-300 mr-2 shrink-0" data-id="element-154">
          <Tag className="w-3 h-3" data-id="element-155" />
        </span>
        <span className="hidden sm:inline text-[15px] font-semibold" data-id="element-156">
          {t('promo.firstOrder')}
        </span>
        <span className="sm:hidden text-[12px] font-semibold" data-id="element-157">
          {t('promo.firstOrderShort')}
        </span>
        <button onClick={handleCopy} className="inline-flex items-center gap-1.5 ml-2 bg-stone-900 hover:bg-stone-800 text-amber-300 px-2.5 py-0.5 rounded-md font-mono font-bold tracking-wider text-[11px] sm:text-xs transition-colors shrink-0" aria-label={`Copy promo code ${PROMO_CODE}`} data-id="element-158">
          {copied ? <>
              <Check className="w-3 h-3" data-id="element-159" />
              {t('promo.copied')}
            </> : <>
              {PROMO_CODE}
              <span className="text-amber-300/70 font-normal hidden sm:inline" data-id="element-160">
                {t('promo.tapToCopy')}
              </span>
            </>}
        </button>
      </>
  };
  return <div className="bg-[#FFCC00] dark:bg-[#FFCC00] text-stone-900 py-1.5 px-4 text-xs sm:text-sm font-medium relative z-[60] border-b border-[#E6B800] dark:border-[#E6B800]" data-id="element-161">
      {/*
                                                Mobile: justify-between keeps the right-side items pinned to the
                                                right edge so they don't shift when the rotating message changes
                                                length. Desktop: centered.
                                              */}
      <div className="max-w-7xl mx-auto flex items-center justify-between md:justify-center gap-3 sm:gap-4" data-id="element-162">
        {/* Rotating left slot */}
        <div className="flex-1 md:flex-none min-w-0 flex items-center min-h-[20px] relative" data-id="element-163">
          <AnimatePresence mode="wait" data-id="element-164">
            <motion.div key={currentSlide.key} initial={{
            opacity: 0,
            y: 6
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -6
          }} transition={{
            duration: 0.35,
            ease: 'easeOut'
          }} className="flex items-center whitespace-nowrap min-w-0 max-w-full" data-id="element-165">
              {currentSlide.content}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Right group — stays static */}
        <div className="flex items-center gap-2 sm:gap-4 shrink-0" data-id="element-166">
          <span className="opacity-40" data-id="element-167">|</span>

          {/* Phone — desktop: icon + number always visible.
            Mobile: icon-only; number slides out on tap, hides after 3s. */}
          <a href="tel:206-619-3742" aria-label={`Call ${PHONE_NUMBER}`} className="hidden md:inline-flex items-center gap-1.5 px-2 h-7 rounded-full hover:bg-stone-900/10 transition-colors text-stone-900 font-semibold text-sm" data-id="element-168">
            <Phone className="w-4 h-4" data-id="element-169" />
            <span data-id="element-170">{PHONE_NUMBER}</span>
          </a>
          <div className="md:hidden relative flex items-center" onClick={() => setPhoneOpen(true)} data-id="element-171">
            <a href="tel:206-619-3742" aria-label={`Call ${PHONE_NUMBER}`} className="flex items-center justify-center w-7 h-7 rounded-full hover:bg-stone-900/10 transition-colors" data-id="element-172">
              <Phone className="w-4 h-4" data-id="element-173" />
            </a>
            <AnimatePresence data-id="element-174">
              {phoneOpen && <motion.span initial={{
              opacity: 0,
              x: -8,
              width: 0
            }} animate={{
              opacity: 1,
              x: 0,
              width: 'auto'
            }} exit={{
              opacity: 0,
              x: -8,
              width: 0
            }} transition={{
              duration: 0.28,
              ease: [0.22, 1, 0.36, 1]
            }} className="ml-1.5 text-stone-900 font-semibold tracking-tight text-xs whitespace-nowrap pointer-events-none overflow-hidden" data-id="element-175">
                  {PHONE_NUMBER}
                </motion.span>}
            </AnimatePresence>
          </div>

          <span className="opacity-40" data-id="element-176">|</span>

          <Link to="/subcontractors" className={`flex items-center transition-colors font-[600] ${isContractors ? 'text-emerald-700 hover:text-emerald-800' : 'text-stone-900 hover:text-stone-700'}`} data-id="element-177">
            <HardHat className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1" data-id="element-178" />
            {t('announce.contractors')}
          </Link>
        </div>
      </div>
    </div>;
}